import { useEffect } from 'react';

import { OutletModel, billingApi, outletsApi, profileApi } from '@pukka/api';
import { assertUtil, dateUtils, numberUtils } from '@pukka/common/utils';
import { queryClient } from '@pukka/tanstack-query';
import { toast } from '@pukka/toast';
import { proxy } from '@pukka/valtio';
import { useQuery } from '@tanstack/react-query';

interface ProrateInfo {
  period: string;
  price: string;
  suffix: string;
}

interface IRecurringOutletState extends Record<string, unknown> {
  isBusinessExpired: boolean | undefined;
  outlets: OutletModel.Outlet[];
  showPrompt: 'deletion' | 'activation' | false;
  selectedOutlet: OutletModel.Outlet | null;
  toggling: 'deletion' | 'activation' | 'immediate' | false;
  prorate: ProrateInfo | null;
  readonly closePrompt: () => void;
  readonly handleOutletActivation: (id: number, isActive: boolean, isImmediate?: boolean) => void;
  readonly handleToggleOutlet: (outlet: OutletModel.Outlet, isActive: boolean) => void;
  readonly useOutletData: () => {
    data: OutletModel.Outlet[];
    isFetched: boolean;
  };
}

const RecurringOutletState: IRecurringOutletState = proxy({
  isBusinessExpired: undefined,
  outlets: [],
  showPrompt: false,
  selectedOutlet: null,
  toggling: false,
  prorate: null,
  closePrompt() {
    RecurringOutletState.showPrompt = false;
    RecurringOutletState.selectedOutlet = null;
    RecurringOutletState.prorate = null;
  },
  async handleOutletActivation(id: number, isActive: boolean, isImmediate?: boolean) {
    try {
      if (isActive && isImmediate) {
        RecurringOutletState.toggling = 'immediate';

        if (!RecurringOutletState.selectedOutlet) {
          throw new Error('Selected outlet not found');
        }

        const { redirect_url: url } = await billingApi.verifyOutletBilling(RecurringOutletState.selectedOutlet.id);

        if (url) {
          window.location.href = url;
        }
      } else {
        RecurringOutletState.toggling = isActive ? 'activation' : 'deletion';
        await billingApi.updateOutletSubscription({ isActive, id });
      }

      await queryClient.invalidateQueries({ queryKey: ['outlet-list'] });
    } catch (e) {
      console.error(e);

      toast.error('Something went wrong. Please try again later');
    }

    RecurringOutletState.toggling = false;
    RecurringOutletState.closePrompt();
  },
  async handleToggleOutlet(outlet: OutletModel.Outlet, isActive: boolean) {
    // prevent user to toggle while updating
    if (RecurringOutletState.selectedOutlet) {
      return;
    }

    RecurringOutletState.selectedOutlet = outlet;

    if (isActive && outlet.isExpired && !RecurringOutletState.isBusinessExpired) {
      RecurringOutletState.showPrompt = 'activation';

      try {
        const data = await billingApi.getOutletBillingCalculation(outlet.id);

        RecurringOutletState.prorate = {
          period: `${dateUtils.formatDate(data.MOKA?.start_date, 'dd/MM/yyyy')} – ${dateUtils.formatDate(
            data.MOKA?.end_date,
            'dd/MM/yyyy',
          )}`,
          price: numberUtils.formatCurrency(data.final_price),
          suffix: '/outlet',
        };
      } catch (error: unknown) {
        RecurringOutletState.closePrompt();

        assertUtil.assertObjectIsTruthy<{
          message: string;
        }>(error);

        toast.error(error.message);
      }
    } else if (isActive) {
      RecurringOutletState.handleOutletActivation(outlet.id, true, false);
    } else {
      RecurringOutletState.showPrompt = 'deletion';
    }
  },
  useOutletData() {
    const { data: profile } = profileApi.useProfile();

    const { data, isFetched } = useQuery({
      queryKey: ['outlet-list'],
      queryFn: () => outletsApi.getOutletList({ active: 'all' }),
      onSuccess(outlets) {
        RecurringOutletState.outlets = outlets;
      },
    });

    useEffect(() => {
      if (profile?.business?.id) {
        if (RecurringOutletState.isBusinessExpired === undefined) {
          RecurringOutletState.isBusinessExpired = profile?.business?.has_expired;
        }
      }
    }, [profile?.business?.id, profile?.business?.has_expired]);

    const activeOutlets =
      data?.filter((outlet: OutletModel.Outlet) => outlet.is_active_subscription || outlet.is_active_subscription === null)?.length ?? 0;
    const hasOneActiveOutlet = activeOutlets <= 1;
    const modifiedData = (data || []).map((outlet: OutletModel.Outlet) => {
      const isActive = outlet.is_active_subscription || outlet.is_active_subscription === null; // note: it's active if the value is true or null

      return {
        ...outlet,
        is_active_subscription: isActive,
        disableToggle: isActive && hasOneActiveOutlet,
        isExpired: new Date(outlet.end_date) < new Date(),
      };
    });

    return {
      data: modifiedData,
      isFetched,
    };
  },
});

export { RecurringOutletState };
